import React, { useMemo } from 'react';
import { Box, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import ReportCardBackground from '@/assets/backgrounds/report-card-background.svg';
import SmallReportCardBackground from '@/assets/backgrounds/small-report-card-background.svg';
import Link from '@/modules/core/components/Link';

import DescriptionBase, { DescriptionBaseProps } from './DescriptionBase';
import ReportFavoriteButton from './ReportFavoriteButton';

export interface ReportCardBaseProps {
  isFavoriteMode?: boolean;
}

interface DescriptionBaseNode {
  DescriptionBase: React.FC<DescriptionBaseProps>;
}

interface WideProps {
  isWideMode?: boolean;
}

interface Props extends WideProps {
  icon: React.ReactNode;
  isNew?: boolean;
  isUpdated?: boolean;
  title: string;
  testId?: string;
  description: string;
  url: string;
  children?: React.ReactNode;
  isFavorite?: boolean;
  onChangeFavorite: (value: boolean) => void;
}

const ButtonText = styled(Text)``;
const ButtonWrapper = styled(Box)`
  transition: all 0.15s;
  &:hover {
    background: ${({ theme }) => theme.colors.buttonSecondaryHoverBorder};
    ${ButtonText} {
      color: ${({ theme }) => theme.colors.textOnColor};
    }
  }
`;

const Content = styled(Box)`
  transition: transform 0.15s;
`;

const IconWrapper = styled(Box)`
  opacity: 1;
  transition: all 0.15s;
`;

const ShowReportsAnchorWrapper = styled(Box)`
  opacity: 0;
  position: absolute;

  transition: all 0.15s;
`;

const FavoriteWrapper = styled(Box)`
  opacity: 0;
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 100;
  transition: all 0.15s;
`;

const Wrapper = styled(Box)<WideProps>`
  overflow: hidden;
  position: relative;
  outline-color: ${({ theme }) => theme.colors.border2};
  outline-style: solid;
  outline-width: 0px;
  background-image: url('${({ isWideMode }) =>
    isWideMode ? ReportCardBackground : SmallReportCardBackground}');
  &:hover {
    outline-width: 1px;

    ${Content} {
      transform: translateY(${({ isWideMode }) => (isWideMode ? '-60px' : '-54px')});
      ${IconWrapper} {
        opacity: 0;
        /* transform: translateY(-5px); */
      }
      ${ShowReportsAnchorWrapper} {
        display: inline-block;
        opacity: 1;
      }
    }
    ${FavoriteWrapper} {
      opacity: 1;
    }
  }
`;

const Title = styled(Text)`
  line-height: 1;
  &:hover {
    color: ${({ theme }) => theme.colors.textAccent};
  }
`;

const ReportCardBase: React.FC<Props> & DescriptionBaseNode = ({
  isWideMode = false,
  isUpdated = false,
  testId = 'report-card-base',
  icon,
  isFavorite = false,
  onChangeFavorite,
  isNew,
  url,
  title,
  description,
  children,
}) => {
  const currentTestId = useMemo(() => {
    if (isWideMode) {
      return `${testId}_favorite`;
    }
    return testId;
  }, [isWideMode, testId]);
  const label = useMemo(() => {
    if (isWideMode) {
      return null;
    }
    if (isNew) {
      return (
        <Box
          round="60px"
          data-testid={`${currentTestId}_is-new`}
          margin={{ left: 'spacing12' }}
          pad={{ vertical: 'spacing2', horizontal: 'spacing12' }}
          background="investmentsProfit"
        >
          <Text weight={500}>New!</Text>
        </Box>
      );
    }
    if (isUpdated) {
      return (
        <Box
          round="60px"
          data-testid={`${currentTestId}_is-updated`}
          margin={{ left: 'spacing12' }}
          pad={{ vertical: 'spacing2', horizontal: 'spacing12' }}
          background="fund5"
        >
          <Text weight={500}>Updated!</Text>
        </Box>
      );
    }
    return null;
  }, [isNew, isUpdated, isWideMode, currentTestId]);
  return (
    <Wrapper
      height={{ min: '250px' }}
      isWideMode={isWideMode}
      background="canvas"
      round="container3Round"
      fill
      data-testid={currentTestId}
      pad={{ vertical: 'spacing24', horizontal: 'spacing32' }}
      border={{ size: 'small', color: 'border2' }}
    >
      <FavoriteWrapper justify="center" align="end">
        <ReportFavoriteButton
          testId={`${currentTestId}_favorite-button`}
          value={isFavorite}
          onChange={onChangeFavorite}
        />
      </FavoriteWrapper>
      <Content>
        <IconWrapper direction="row" align="center">
          {icon}
          {label}
        </IconWrapper>
        <Box direction="row" margin={{ top: 'spacing12' }} align="center">
          <Link testId={`${currentTestId}_title`} to={url}>
            <Title size="large" color="textTitle" weight={700}>
              {title}
            </Title>
          </Link>
        </Box>
        <Box width={{ min: '60px' }}>
          <Text
            data-testid={`${currentTestId}_text`}
            margin={{ top: 'spacing2' }}
            size="small"
            color="textSecondary"
            weight={500}
          >
            {description}
          </Text>
        </Box>
        <Box direction={isWideMode ? 'row' : 'column'}>
          <Box width={{ min: '250px' }}>{children}</Box>
        </Box>
        <Box style={{ position: 'relative' }}>
          <ShowReportsAnchorWrapper margin={{ top: 'spacing12' }}>
            <Link testId={`${currentTestId}_show-more`} to={url}>
              <ButtonWrapper
                round="button1Round"
                background="transparent"
                border={{ size: '2px', color: 'buttonSecondaryHoverBorder' }}
                pad={{ vertical: '10px', horizontal: 'spacing16' }}
              >
                <ButtonText size="medium" color="textBody" weight={700}>
                  Show Report
                </ButtonText>
              </ButtonWrapper>
            </Link>
          </ShowReportsAnchorWrapper>
        </Box>
      </Content>
    </Wrapper>
  );
};

ReportCardBase.DescriptionBase = DescriptionBase;
export default ReportCardBase;
